<template>
  <aside v-if="actions.length" class="c-float-action-widget">
    <template v-for="(action, index) in actions" :key="action.id || index">
      <button
        v-if="isScrollTop(action)"
        class="c-float-action-widget__item"
        :class="itemClass(action, index)"
        type="button"
        :aria-label="getAriaLabel(action)"
        @click="scrollToTop"
      >
        <span
          v-if="getIconSvgCode(action)"
          class="c-float-action-widget__icon"
          aria-hidden="true"
          v-html="getIconSvgCode(action)"
        />
        <SiteImage v-else class="c-float-action-widget__image" :field="getIconField(action)" />
      </button>
      <div
        v-else-if="isChatbot(action)"
        class="c-float-action-widget__item"
        :class="[itemClass(action, index), 'c-float-action-widget__item--chatbot']"
        :rel="getRel(action)"
        :aria-label="getAriaLabel(action)"
        @click="handleClick"
      >
        <span
          v-if="getIconSvgCode(action)"
          class="c-float-action-widget__icon"
          aria-hidden="true"
          v-html="getIconSvgCode(action)"
        />
        <SiteImage v-else class="c-float-action-widget__image" :field="getIconField(action)" />
      </div>
      <a
        v-else
        class="c-float-action-widget__item"
        :class="itemClass(action, index)"
        :href="getHref(action)"
        :target="getTarget(action)"
        :rel="getRel(action)"
        :aria-label="getAriaLabel(action)"
      >
        <span
          v-if="getIconSvgCode(action)"
          class="c-float-action-widget__icon"
          aria-hidden="true"
          v-html="getIconSvgCode(action)"
        />
        <SiteImage v-else class="c-float-action-widget__image" :field="getIconField(action)" />
      </a>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type {
  FloatActionWidgetActionFields,
  FloatActionWidgetProps,
} from '@/types/components/FloatActionWidget';
import type { ScItem } from '@/types/sitecore';

const props = defineProps<FloatActionWidgetProps>();

const actions = computed(() => props.fields?.actions ?? []);
const defaultVariants = ['light', 'green', 'light'];

const getFieldValue = (action: ScItem<FloatActionWidgetActionFields>, fieldName: string) => {
  if (action.fields == null) return undefined;
  const field = action.fields[fieldName as keyof FloatActionWidgetActionFields];
  return field?.value;
};

const getIconField = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields == null) return undefined;
  return action.fields.icon;
};
const getIconSvgCode = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields == null) return '';
  return action.fields.icon?.value?.svgCode || '';
};
const getIconAlt = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields == null) return '';
  return action.fields.icon?.value?.alt || '';
};
const getAriaLabel = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields?.link?.value?.text) return action.fields.link.value.text;
  const alt = getIconAlt(action);
  if (alt) return alt;
  return isScrollTop(action) ? 'Back to top' : 'Action';
};
const getVariant = (action: ScItem<FloatActionWidgetActionFields>, index: number) => {
  const variant = getFieldValue(action, 'variant');
  return variant || defaultVariants[index % defaultVariants.length];
};
const getHref = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields?.link?.value?.href) return action.fields.link.value.href;
  return '#';
};
const getTarget = (action: ScItem<FloatActionWidgetActionFields>) => {
  if (action.fields?.link?.value?.target) return action.fields.link.value.target;
  return undefined;
};
const getRel = (action: ScItem<FloatActionWidgetActionFields>) =>
  getTarget(action) === '_blank' ? 'noopener noreferrer' : undefined;
const isScrollTop = (action: ScItem<FloatActionWidgetActionFields>) =>
  getFieldValue(action, 'actionType') === 'scrollTop';
const isChatbot = (action: ScItem<FloatActionWidgetActionFields>) =>
  getFieldValue(action, 'actionType') === 'chatbot';
const itemClass = (action: ScItem<FloatActionWidgetActionFields>, index: number) =>
  `c-float-action-widget__item--${getVariant(action, index)}`;
const scrollToTop = () => {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
const handleClick = async () => {
  console.log('点击了 Chatbot 按钮');
  const chatInstance = (window as any).chatInstance;
  if (!chatInstance) {
    console.warn('Chatbot 未初始化完成');
    return;
  }
  try {
    await chatInstance.open();
  } catch (err) {
    console.error('唤起失败:', err);
  }
};
</script>

<style lang="scss">
@import '../../styles/variables';
.c-float-action-widget {
  position: fixed;
  right: 22px;
  bottom: 182px;
  z-index: 40;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;

  &__item {
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #3f4441;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
    cursor: pointer;
    pointer-events: auto;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      background-color 0.2s ease;

    &:hover,
    &:focus-visible {
      transform: translateY(-2px);
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
      text-decoration: none;
    }

    &--light {
      background: $white;
      color: #3f4441;
    }

    &--green {
      background: #6f9877;
      color: $white;
    }

    &--blue {
      background: $white;
      color: #2fa7d7;
    }
  }

  &__icon,
  &__icon img,
  &__icon svg {
    width: 28px;
    height: 28px;
    display: block;
    object-fit: contain;
  }

  &__image {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 50%;
    object-fit: cover;
  }

  &__item--blue &__icon,
  &__item--blue &__icon img,
  &__item--blue &__icon svg {
    width: 28px;
    height: 28px;
  }

  // chatbot 类型 icon 尺寸：45px * 45px
  &__item--chatbot &__icon,
  &__item--chatbot &__icon img,
  &__item--chatbot &__icon svg,
  &__item--chatbot &__image {
    width: 45px;
    height: 45px;
  }

  @media (max-width: 840px) {
    right: 14px;
    bottom: 122px;
    gap: 8px;

    &__item {
      width: 38px;
      height: 38px;
    }

    &__icon,
    &__icon img,
    &__icon svg {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
